import { Modal, ModalProps } from '@billy/design-system'
import { useCallback, useMemo } from 'react'
import ReactCSSTransitionReplace from 'react-css-transition-replace'
import { useTranslation } from 'react-i18next'

import { useModalContentToggler } from '../../contexts/modalContentTogglerContext'

interface ToggleModalProps extends ModalProps {
  transition?: string
  withTransition?: boolean
}

export const ToggleModal = ({ transition = 'fade-wait', withTransition = true, ...modalProps }: ToggleModalProps) => {
  const { activeState, activeStateModalData, prevState, setActiveState } = useModalContentToggler()
  const { t } = useTranslation()

  const backwardsNavigationState = useMemo(() => {
    return activeStateModalData?.backwardsNavigationStateId || prevState
  }, [activeStateModalData?.backwardsNavigationStateId, prevState])

  const shouldDisplayBackButton =
    !!activeStateModalData?.withBackwardsNavigation &&
    !!backwardsNavigationState &&
    backwardsNavigationState !== activeState

  const handleBackButtonClick = useCallback(() => {
    setActiveState(backwardsNavigationState)
  }, [backwardsNavigationState, setActiveState])

  const modalTitle = useMemo(
    () => activeStateModalData?.headerTitle ?? t(activeStateModalData?.headerTKey as string),
    [activeStateModalData?.headerTKey, activeStateModalData?.headerTitle, t],
  )

  return (
    <Modal {...modalProps}>
      <Modal.Header
        onBackButtonClick={handleBackButtonClick}
        title={modalTitle}
        withBackButton={shouldDisplayBackButton}
      />
      <Modal.Body>
        {withTransition ? (
          <ReactCSSTransitionReplace
            transitionName={transition}
            transitionEnterTimeout={500}
            transitionLeaveTimeout={500}
          >
            <div key={activeStateModalData?.id}>{activeStateModalData?.modalBody}</div>
          </ReactCSSTransitionReplace>
        ) : (
          <div key={activeStateModalData?.id}>{activeStateModalData?.modalBody}</div>
        )}
      </Modal.Body>

      {activeStateModalData?.modalFooter && <Modal.Footer>{activeStateModalData?.modalFooter}</Modal.Footer>}
    </Modal>
  )
}
